/**
 * Keyword density from stems/roots, not exact phrase match.
 * A hit is a heading or sentence (~12–20 tokens) where the keyword's
 * content stems co-occur. Search engines score natural inflected wording;
 * exact token stuffing is not required.
 */

export const STEM_WINDOW_SIZE = 16;

const CS_STOPWORDS = new Set([
    'a', 'i', 'o', 'u', 'v', 've', 'z', 'ze', 's', 'se', 'k', 'ke',
    'na', 'do', 'od', 'po', 'pro', 'pri', 'pred', 'pres', 'za', 'bez',
    'mezi', 'pod', 'nad', 'jak', 'co', 'ci', 'ale', 'nebo', 'ani',
    'uz', 'jen', 'az', 'tak', 'ze', 'je', 'to', 'ta', 'ty', 'ten',
    'tato', 'toto', 'tyto', 'jeho', 'jeji', 'jejich', 'si', 'mi', 'ti',
    'aby', 'kdyz', 'pokud', 'nebo', 'vs', 'viz',
]);

const EN_STOPWORDS = new Set([
    'a', 'an', 'the', 'and', 'or', 'of', 'to', 'in', 'on', 'for', 'with',
    'at', 'by', 'from', 'as', 'is', 'are', 'was', 'were', 'be', 'been',
    'it', 'this', 'that', 'these', 'those', 'into', 'about', 'over',
    'after', 'before', 'vs', 'via', 'per',
]);

export function normalizeKeywordPhrase(value: string | null | undefined): string {
    return String(value || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

export function languageKey(languageCode?: string | null): 'cs' | 'en' {
    const lang = String(languageCode || 'cs').slice(0, 2).toLowerCase();
    if (lang === 'cs' || lang === 'sk') {
        return 'cs';
    }
    return 'en';
}

function stopwordsFor(lang: 'cs' | 'en'): Set<string> {
    return lang === 'cs' ? CS_STOPWORDS : EN_STOPWORDS;
}

/** Single Czech or English function word. Multi-word phrases are never function words. */
export function isStandaloneFunctionWord(phrase: string | null | undefined): boolean {
    const key = normalizeKeywordPhrase(phrase);
    if (!key || key.includes(' ')) {
        return false;
    }
    const folded = foldDiacritics(key);
    return CS_STOPWORDS.has(folded) || EN_STOPWORDS.has(folded);
}

export function foldDiacritics(value: string): string {
    return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

export function tokenizeWords(value: string): string[] {
    return String(value || '')
        .split(/[^\p{L}\p{N}]+/u)
        .filter(Boolean);
}

function isAcronym(token: string): boolean {
    const letters = token.replace(/[^A-Za-zÁÉÍÓÚŮÝČĎĚŇŘŠŤŽáéíóúůýčďěňřšťž]/g, '');
    return letters.length >= 2 && letters.length <= 6 && letters === letters.toUpperCase();
}

function englishStem(folded: string): string {
    let t = folded;
    if (t.length > 4 && /ies$/.test(t)) {
        return t.replace(/ies$/, 'y');
    }
    if (t.length > 5 && /ing$/.test(t)) {
        t = t.slice(0, -3);
        if (t.length > 3 && t[t.length - 1] === t[t.length - 2]) {
            t = t.slice(0, -1);
        }
        return t;
    }
    if (t.length > 4 && /ed$/.test(t)) {
        return t.slice(0, -2);
    }
    if (t.length > 5 && /ly$/.test(t)) {
        return t.slice(0, -2);
    }
    if (t.length > 4 && /(ches|shes|sses|xes|zes)$/.test(t)) {
        return t.replace(/es$/, '');
    }
    if (t.length > 3 && /s$/.test(t) && !/ss$/.test(t)) {
        return t.slice(0, -1);
    }
    return t;
}

const CS_SUFFIXES = [
    'ami', 'ach', 'ich', 'ovi', 'ove', 'ech', 'ama', 'imi', 'ych', 'ymi',
    'emu', 'um', 'ou', 'em', 'am', 'im', 'ho', 'mu', 'mi', 'ma',
];

function czechStem(folded: string): string {
    let t = folded;
    if (t.length <= 3) {
        return t;
    }
    for (const suffix of CS_SUFFIXES) {
        if (t.endsWith(suffix) && t.length - suffix.length >= 3) {
            t = t.slice(0, -suffix.length);
            break;
        }
    }
    if (t.length >= 4 && /[aeiouy]$/.test(t)) {
        t = t.slice(0, -1);
    }
    return t;
}

export function stemToken(token: string, languageCode?: string | null): string {
    const raw = String(token || '').trim();
    if (!raw) {
        return '';
    }
    const folded = foldDiacritics(raw).toLowerCase();
    if (isAcronym(raw) || folded.length <= 3) {
        return folded;
    }
    return languageKey(languageCode) === 'en' ? englishStem(folded) : czechStem(folded);
}

const PALATAL: Record<string, string> = {
    h: 'z',
    z: 'h',
    k: 'c',
    c: 'k',
    r: 'r',
};

export function stemsEquivalent(a: string, b: string): boolean {
    if (!a || !b) {
        return false;
    }
    if (a === b) {
        return true;
    }
    const shorter = a.length <= b.length ? a : b;
    const longer = a.length <= b.length ? b : a;
    if (shorter.length >= 4 && longer.startsWith(shorter)) {
        return true;
    }
    if (a.length >= 3 && b.length >= 3 && a.slice(0, -1) === b.slice(0, -1)) {
        const left = a.slice(-1);
        const right = b.slice(-1);
        if (PALATAL[left] === right) {
            return true;
        }
        // Brno/Brně already equal after vowel strip; 3-letter psy/psa:
        if (a.length === 3 && b.length === 3) {
            return true;
        }
    }
    return false;
}

export interface KeywordStem {
    original: string;
    stem: string;
    distinctive: boolean;
}

export function keywordContentStems(keyword: string, languageCode?: string | null): KeywordStem[] {
    const lang = languageKey(languageCode);
    const stops = stopwordsFor(lang);
    const seen = new Set<string>();
    const result: KeywordStem[] = [];
    for (const token of tokenizeWords(keyword)) {
        const folded = foldDiacritics(token).toLowerCase();
        if (!folded || stops.has(folded)) {
            continue;
        }
        const stem = stemToken(token, lang);
        if (!stem || seen.has(stem)) {
            continue;
        }
        seen.add(stem);
        result.push({
            original: token,
            stem,
            distinctive: isAcronym(token) || stem.length >= 4,
        });
    }
    return result;
}

function windowMatchesStemmed(windowStems: string[], stems: KeywordStem[]): boolean {
    if (!stems.length) {
        return false;
    }
    const matched = stems.filter((item) =>
        windowStems.some((windowStem) => stemsEquivalent(item.stem, windowStem))
    );
    if (!matched.length) {
        return false;
    }
    const anchors = stems.filter((item) => item.distinctive);
    if (anchors.length) {
        const missingAnchor = anchors.some((anchor) =>
            !matched.some((item) => item.stem === anchor.stem)
        );
        if (missingAnchor) {
            return false;
        }
    } else if (matched.length < stems.length) {
        return false;
    }
    const needed = Math.max(1, Math.ceil((stems.length * 2) / 3));
    return matched.length >= needed;
}

function textWindows(text: string): string[][] {
    const chunks = String(text || '').split(/\s*\|\s*|[.!?]+(?:\s+|$)|[\n\r]+/);
    const windows: string[][] = [];
    for (const chunk of chunks) {
        const tokens = tokenizeWords(chunk);
        if (!tokens.length) {
            continue;
        }
        if (tokens.length <= 20) {
            windows.push(tokens);
            continue;
        }
        for (let index = 0; index < tokens.length; index += STEM_WINDOW_SIZE) {
            const slice = tokens.slice(index, index + STEM_WINDOW_SIZE);
            if (slice.length) {
                windows.push(slice);
            }
        }
    }
    return windows;
}

type StemmedWindow = {stems: string[]};

let stemmedWindowCacheText = '';
let stemmedWindowCacheLang = '';
let stemmedWindowCache: StemmedWindow[] | null = null;

function stemmedTextWindows(text: string, languageCode?: string | null): StemmedWindow[] {
    const lang = languageKey(languageCode);
    if (stemmedWindowCache && stemmedWindowCacheText === text && stemmedWindowCacheLang === lang) {
        return stemmedWindowCache;
    }
    const windows = textWindows(text).map((tokens) => ({
        stems: tokens.map((token) => stemToken(token, lang)).filter(Boolean),
    }));
    stemmedWindowCacheText = text;
    stemmedWindowCacheLang = lang;
    stemmedWindowCache = windows;
    return windows;
}

/**
 * One tokenize/stem pass over the article, then hit-count every phrase.
 * Use this instead of calling countStemKeywordHits in a loop on input.
 */
export function countStemKeywordHitsForPhrases(
    text: string,
    phrases: Array<string | null | undefined>,
    languageCode?: string | null
): Record<string, number> {
    const result: Record<string, number> = {};
    const unique: string[] = [];
    for (const raw of phrases) {
        const phrase = String(raw || '').replace(/\s+/g, ' ').trim();
        if (!phrase || Object.prototype.hasOwnProperty.call(result, phrase)) {
            continue;
        }
        result[phrase] = 0;
        unique.push(phrase);
    }
    if (!unique.length || !String(text || '').trim()) {
        return result;
    }
    const windows = stemmedTextWindows(text, languageCode);
    for (const phrase of unique) {
        const stems = keywordContentStems(phrase, languageCode);
        if (!stems.length) {
            continue;
        }
        result[phrase] = windows.filter((window) => windowMatchesStemmed(window.stems, stems)).length;
    }
    return result;
}

/**
 * Count how many headings/sentences (or ~16-token windows) contain the
 * keyword's content stems. Comparable to the old exact-match hit count
 * used by density % = hits / articleWordCount * 100.
 */
export function countStemKeywordHits(
    text: string,
    keyword: string,
    languageCode?: string | null
): number {
    const phrase = String(keyword || '').replace(/\s+/g, ' ').trim();
    if (!phrase || !String(text || '').trim()) {
        return 0;
    }
    return countStemKeywordHitsForPhrases(text, [phrase], languageCode)[phrase] || 0;
}

export function textCoversKeyword(
    text: string,
    keyword: string,
    languageCode?: string | null
): boolean {
    if (countStemKeywordHits(text, keyword, languageCode) > 0) {
        return true;
    }
    if (!languageCode) {
        return countStemKeywordHits(text, keyword, 'en') > 0;
    }
    return false;
}

export function anyKeywordCovered(
    text: string,
    keywords: Array<string | null | undefined>,
    languageCode?: string | null
): boolean {
    return (keywords || []).some((item) => textCoversKeyword(text, String(item || ''), languageCode));
}

export interface StemTokenRange {
    start: number;
    end: number;
    token: string;
}

/** Same tokens as tokenizeWords, with character offsets for highlighter markers. */
export function tokenizeWordsWithOffsets(value: string): StemTokenRange[] {
    const text = String(value || '');
    const result: StemTokenRange[] = [];
    const re = /[\p{L}\p{N}]+/gu;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
        result.push({
            token: match[0],
            start: match.index,
            end: match.index + match[0].length,
        });
    }
    return result;
}

export function tokenMatchesContentStems(
    token: string,
    stems: KeywordStem[],
    languageCode?: string | null
): boolean {
    if (!stems.length) {
        return false;
    }
    const tokenStem = stemToken(token, languageCode);
    if (!tokenStem) {
        return false;
    }
    return stems.some((item) => stemsEquivalent(item.stem, tokenStem));
}

export function findStemTokenRangesForStems(
    text: string,
    stems: KeywordStem[],
    languageCode?: string | null
): StemTokenRange[] {
    if (!stems.length) {
        return [];
    }
    const ranges: StemTokenRange[] = [];
    for (const item of tokenizeWordsWithOffsets(text)) {
        if (tokenMatchesContentStems(item.token, stems, languageCode)) {
            ranges.push(item);
        }
    }
    return ranges;
}

/**
 * Whole inflected tokens whose stem matches the keyword's content stems.
 * Stopword-only keywords (e.g. `pro`) return no ranges.
 */
export function findStemTokenRanges(
    text: string,
    keyword: string,
    languageCode?: string | null
): StemTokenRange[] {
    return findStemTokenRangesForStems(
        text,
        keywordContentStems(keyword, languageCode),
        languageCode
    );
}

function wrapStemTokenRanges(
    text: string,
    ranges: StemTokenRange[],
    wrap: (token: string) => string
): string {
    if (!ranges.length) {
        return text;
    }
    let out = '';
    let last = 0;
    for (const range of ranges) {
        out += text.slice(last, range.start) + wrap(text.slice(range.start, range.end));
        last = range.end;
    }
    return out + text.slice(last);
}

/** Wrap inflected content-stem tokens in HTML text nodes; leave tags and stopwords alone. */
export function highlightKeywordStemsInHtml(
    html: string,
    keyword: string,
    markerClass: string,
    languageCode?: string | null
): string {
    const stems = keywordContentStems(keyword, languageCode);
    if (!stems.length || !html) {
        return html;
    }
    return String(html).replace(/(<[^>]+>)|([^<]+)/g, (chunk, tag: string, text: string) => {
        if (tag || text == null) {
            return chunk;
        }
        return wrapStemTokenRanges(
            text,
            findStemTokenRangesForStems(text, stems, languageCode),
            (token) => `<mark class="${markerClass}">${token}</mark>`
        );
    });
}
